import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import * as Icon from "@phosphor-icons/react/dist/ssr";

/** WhatsApp Web / mobile–aligned palette (brand greens + chat neutrals). */
const WA = {
  header: "#075E54",
  headerDeep: "#064E47",
  headerIconBg: "rgba(255,255,255,0.2)",
  chatWallpaper: "#ECE5DD",
  bubbleIncoming: "#FFFFFF",
  textPrimary: "#111B21",
  textSecondary: "#667781",
  metaTime: "#667781",
  productAccent: "#075E54",
  composerBar: "#F0F2F5",
  inputBorder: "rgba(11, 20, 26, 0.12)",
  send: "#25D366",
  sendHover: "#20BD5A",
  panelBorder: "rgba(7, 94, 84, 0.18)",
} as const;

/** Inline — Tailwind arbitrary classes break on commas inside min()/max(). */
const WA_BACKDROP_STYLE: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 9998,
  backgroundColor: "rgba(17, 27, 33, 0.35)",
  cursor: "default",
  border: "none",
  padding: 0,
};

const WA_PANEL_STYLE: CSSProperties = {
  position: "fixed",
  zIndex: 9999,
  right: "max(1rem, env(safe-area-inset-right, 0px))",
  bottom: "max(5.5rem, calc(1rem + env(safe-area-inset-bottom, 0px) + 5rem))",
  width: "min(380px, calc(100vw - 2rem))",
  maxHeight: "min(85vh, calc(100vh - 8rem))",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: "1rem",
  border: `1px solid ${WA.panelBorder}`,
  backgroundColor: WA.chatWallpaper,
  boxShadow:
    "0 12px 28px rgba(11, 20, 26, 0.18), 0 2px 8px rgba(7, 94, 84, 0.08)",
  pointerEvents: "auto",
};

/** Digits only (no +), for wa.me links */
export const DEFAULT_WHATSAPP_DIGITS = "212664045333";

export type WhatsAppProductMeta = {
  id: string | number;
  name: string;
};

export type WhatsAppChatOpenOptions = {
  phoneDigits?: string;
  initialMessage?: string;
  product?: WhatsAppProductMeta | null;
};

/** Short text shown in the composer for product chats; product ref is appended only when sending. */
const SHORT_PRODUCT_COMPOSER_DEFAULT = "Bonjour !\n\n";

const GENERIC_MESSAGE =
  "Bonjour ! Je souhaite un conseil avant d’acheter — pouvez-vous m’aider ?";

function appendProductMetaForSend(
  userText: string,
  product: WhatsAppProductMeta
): string {
  const body = userText.trim() || "Bonjour !";
  return `${body}\n\n────────\nProduit : « ${product.name} »\nRéf. : ${product.id}`;
}

/** Short two-tone chime when the popup opens (Web Audio API — no audio file). */
function playWhatsAppPopupSound(): void {
  try {
    const w = typeof window !== "undefined" ? window : undefined;
    if (!w) return;
    const AC =
      w.AudioContext ||
      (w as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) return;

    const ctx = new AC();

    const run = () => {
      const playTone = (freq: number, delaySec: number, durSec: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.value = freq;
        const t0 = ctx.currentTime + delaySec;
        gain.gain.setValueAtTime(0, t0);
        gain.gain.linearRampToValueAtTime(0.07, t0 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0008, t0 + durSec);
        osc.start(t0);
        osc.stop(t0 + durSec + 0.03);
      };
      playTone(784, 0, 0.1);
      playTone(988, 0.08, 0.12);
      window.setTimeout(() => {
        try {
          ctx.close();
        } catch {
          /* ignore */
        }
      }, 450);
    };

    void ctx.resume().then(run).catch(() => {
      try {
        run();
      } catch {
        /* autoplay / unsupported */
      }
    });
  } catch {
    /* ignore */
  }
}

type WhatsAppChatContextValue = {
  isOpen: boolean;
  openChat: (opts?: WhatsAppChatOpenOptions) => void;
  closeChat: () => void;
  toggleFabChat: () => void;
  /** When set (e.g. from product detail page), the floating WhatsApp button opens product-aware chat */
  setFloatingProductPageContext: (product: WhatsAppProductMeta | null) => void;
};

const WhatsAppChatContext = createContext<WhatsAppChatContextValue | undefined>(
  undefined
);

export const WhatsAppChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [phoneDigits, setPhoneDigits] = useState(DEFAULT_WHATSAPP_DIGITS);
  const [product, setProduct] = useState<WhatsAppProductMeta | null>(null);
  const [message, setMessage] = useState(GENERIC_MESSAGE);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  /** Latest product while user is on a product page — used when opening chat via floating button only */
  const floatingPageProductRef = useRef<WhatsAppProductMeta | null>(null);

  const setFloatingProductPageContext = useCallback(
    (p: WhatsAppProductMeta | null) => {
      floatingPageProductRef.current = p;
    },
    []
  );

  const openChat = useCallback((opts?: WhatsAppChatOpenOptions) => {
    const phone = opts?.phoneDigits ?? DEFAULT_WHATSAPP_DIGITS;
    const prod = opts?.product ?? null;
    let initial = opts?.initialMessage;
    if (initial === undefined) {
      initial = prod ? SHORT_PRODUCT_COMPOSER_DEFAULT : GENERIC_MESSAGE;
    }
    setPhoneDigits(phone);
    setProduct(prod);
    setMessage(initial);
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleFabChat = useCallback(() => {
    setIsOpen((wasOpen) => {
      if (wasOpen) {
        return false;
      }
      const pageProduct = floatingPageProductRef.current;
      // Apply in same tick as opening — avoids stale composer when not using updater batching
      setPhoneDigits(DEFAULT_WHATSAPP_DIGITS);
      setProduct(pageProduct);
      setMessage(pageProduct ? SHORT_PRODUCT_COMPOSER_DEFAULT : GENERIC_MESSAGE);
      return true;
    });
  }, []);

  const COMPOSER_MAX_PX = 260;

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el || !isOpen) return;
    el.style.height = "0px";
    const next = Math.min(el.scrollHeight, COMPOSER_MAX_PX);
    el.style.height = `${next}px`;
  }, [message, isOpen, product?.id]);

  useEffect(() => {
    if (!isOpen || !textareaRef.current) return;
    const el = textareaRef.current;
    el.focus();
    const len = el.value.length;
    el.selectionStart = len;
    el.selectionEnd = len;
  }, [isOpen, product?.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeChat();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeChat]);

  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      playWhatsAppPopupSound();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  const sendWhatsApp = () => {
    const draft = message.trim() || "Bonjour !";
    const textToSend = product
      ? appendProductMetaForSend(draft, product)
      : draft;
    const whatsappUrl = `https://wa.me/${phoneDigits}?text=${encodeURIComponent(
      textToSend
    )}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  const mount =
    typeof document !== "undefined" ? document.body : null;

  const chatUi =
    isOpen &&
    mount &&
    createPortal(
      <>
        <button
          type="button"
          style={WA_BACKDROP_STYLE}
          aria-label="Fermer"
          onClick={closeChat}
        />
        <div
          style={WA_PANEL_STYLE}
          className="min-h-0"
          role="dialog"
          aria-modal="true"
          aria-labelledby="whatsapp-chat-title"
        >
          <div
            className="flex shrink-0 items-center gap-3 px-3 py-3 text-white"
            style={{
              background: `linear-gradient(180deg, ${WA.header} 0%, ${WA.headerDeep} 100%)`,
            }}
          >
            <button
              type="button"
              onClick={closeChat}
              className="rounded-full p-1 transition-colors hover:bg-white/15 md:hidden"
              aria-label="Fermer"
            >
              <Icon.ArrowLeft size={22} weight="bold" />
            </button>
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: WA.headerIconBg }}
            >
              <Icon.Headset size={22} weight="bold" className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div
                id="whatsapp-chat-title"
                className="truncate text-[15px] font-semibold leading-tight"
              >
                Kissariti · Support
              </div>
              <div className="truncate text-xs text-white/90">
                en ligne · réponse rapide
              </div>
            </div>
            <button
              type="button"
              onClick={closeChat}
              className="hidden rounded-full p-2 transition-colors hover:bg-white/15 md:flex"
              aria-label="Fermer"
            >
              <Icon.X size={20} weight="bold" />
            </button>
          </div>

          <div
            className="min-h-0 flex-1 overflow-y-auto px-3 py-4"
            style={{ backgroundColor: WA.chatWallpaper }}
          >
            <div className="mb-4 flex justify-start">
              <div
                className="max-w-[85%] rounded-lg rounded-tl-none px-3 py-2 text-[14px] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]"
                style={{
                  backgroundColor: WA.bubbleIncoming,
                  color: WA.textPrimary,
                }}
              >
                <p className="leading-snug">
                  {product ? (
                    <>
                      Vous hésitez encore sur{" "}
                      <span
                        className="font-semibold"
                        style={{ color: WA.productAccent }}
                      >
                        « {product.name} »
                      </span>
                      {" "}
                      <span
                        className="text-[13px]"
                        style={{ color: WA.textSecondary }}
                      >
                        (réf. {String(product.id)})
                      </span>
                      ? Notre équipe est là pour vous répondre vite sur les{" "}
                      <span className="font-medium">couleurs</span>,{" "}
                      <span className="font-medium">tailles</span>,{" "}
                      <span className="font-medium">coupe</span>,{" "}
                      <span className="font-medium">type</span> de pièce,
                      matière — posez votre question ci-dessous,
                      nous vous guidons avec plaisir.
                    </>
                  ) : (
                    <>
                      Bonjour ! Décrivez votre demande ci-dessous, puis touchez
                      envoyer pour ouvrir WhatsApp avec votre message.
                    </>
                  )}
                </p>
                <span
                  className="mt-1 block text-right text-[11px] tabular-nums"
                  style={{ color: WA.metaTime }}
                >
                  {new Date().toLocaleTimeString("fr-FR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </div>
          </div>

          <div
            className="flex shrink-0 items-center gap-2.5 border-t border-solid px-3 py-2.5"
            style={{
              backgroundColor: WA.composerBar,
              borderColor: WA.inputBorder,
            }}
          >
            <div
              className="flex min-h-[48px] min-w-0 flex-1 items-center rounded-[1.35rem] border bg-white px-3 py-1.5 shadow-inner"
              style={{ borderColor: WA.inputBorder }}
            >
              <textarea
                ref={textareaRef}
                id="whatsapp-compose"
                rows={1}
                value={message}
                aria-label="Votre message"
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendWhatsApp();
                  }
                }}
                placeholder={
                  product
                    ? "Ex. : Quelle taille me conseillez-vous pour…"
                    : "Écrivez votre message…"
                }
                className="wa-chat-composer-field box-border max-h-[260px] min-h-[40px] w-full overflow-y-auto overflow-x-hidden bg-transparent text-[15px] leading-snug outline-none placeholder:text-[#8696A0]"
                style={{ color: WA.textPrimary }}
              />
            </div>
            <button
              type="button"
              onClick={sendWhatsApp}
              className="flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-full text-white shadow-md transition-colors disabled:opacity-50"
              style={{ backgroundColor: WA.send }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = WA.sendHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = WA.send;
              }}
              aria-label="Envoyer sur WhatsApp"
            >
              <Icon.PaperPlaneRight
                size={22}
                weight="fill"
                className="translate-x-0.5"
              />
            </button>
          </div>
        </div>
      </>,
      document.body
    );

  return (
    <WhatsAppChatContext.Provider
      value={{
        isOpen,
        openChat,
        closeChat,
        toggleFabChat,
        setFloatingProductPageContext,
      }}
    >
      {children}
      {chatUi}
    </WhatsAppChatContext.Provider>
  );
};

export function useWhatsAppChat(): WhatsAppChatContextValue {
  const ctx = useContext(WhatsAppChatContext);
  if (!ctx) {
    throw new Error("useWhatsAppChat must be used within WhatsAppChatProvider");
  }
  return ctx;
}
